import {
  Children,
  Fragment,
  isValidElement,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type RefObject,
} from "react"
import { cn } from "cn"

const HIGHLIGHT_COVER_START = 0.18
const HIGHLIGHT_COVER_END = 0.46

export function scrollHighlightProgress(
  elementTop: number,
  elementHeight: number,
  viewportHeight: number,
  coverStart = HIGHLIGHT_COVER_START,
  coverEnd = HIGHLIGHT_COVER_END
) {
  const coverDistance = viewportHeight + elementHeight
  if (coverDistance <= 0 || coverEnd <= coverStart) return 1
  const cover = (viewportHeight - elementTop) / coverDistance
  return Math.min(
    1,
    Math.max(0, (cover - coverStart) / (coverEnd - coverStart))
  )
}

export function flattenHighlightText(children: ReactNode): string {
  if (typeof children === "string" || typeof children === "number") {
    return String(children)
  }
  if (Array.isArray(children)) {
    return children.map(flattenHighlightText).join("")
  }
  return ""
}

export function highlightWords(text: string) {
  const normalized = text.replace(/\s+/g, " ").trim()
  if (!normalized) return []

  const words = normalized.split(" ")
  const total = normalized.length
  let cursor = 0

  return words.map((word, index) => {
    const start = cursor / total
    const end = (cursor + word.length) / total
    cursor += word.length + (index === words.length - 1 ? 0 : 1)
    return {
      word,
      start,
      end,
      display: index === words.length - 1 ? word : `${word} `,
    }
  })
}

function useScrollHighlight(ref: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    let frame = 0

    const update = () => {
      const rect = el.getBoundingClientRect()
      const progress = scrollHighlightProgress(
        rect.top,
        rect.height,
        window.innerHeight
      )
      el.style.setProperty("--scroll-highlight", progress.toFixed(4))
    }

    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        update()
      })
    }

    update()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [ref])
}

function flattenFragments(children: ReactNode): ReactNode[] {
  return Children.toArray(children).flatMap((child) =>
    isValidElement<{ children?: ReactNode }>(child) && child.type === Fragment
      ? flattenFragments(child.props.children)
      : [child]
  )
}

function HighlightWord({
  start,
  end,
  children,
}: {
  start: number
  end: number
  children: string
}) {
  return (
    <span
      ref={(el) => {
        if (!el) return
        el.style.setProperty("--start", String(start))
        el.style.setProperty("--end", String(end))
      }}
    >
      {children}
    </span>
  )
}

/**
 * Splits string children into highlightable words mapped onto the
 * `[from, to]` slice of the surrounding group's scroll progress. Non-string
 * children (e.g. `<br />`) are rendered in place.
 */
export function HighlightWords({
  children,
  from = 0,
  to = 1,
}: {
  children: ReactNode
  from?: number
  to?: number
}) {
  const parts = flattenFragments(children)
  const text = parts
    .map((part) => (typeof part === "string" ? part : " "))
    .join("")
  const words = highlightWords(text)
  const scale = (value: number) => from + value * (to - from)

  let wordIndex = 0
  return parts.map((part, partIndex) => {
    if (typeof part !== "string") {
      return isValidElement(part) ? part : null
    }
    const normalized = part.replace(/\s+/g, " ")
    const partWords = normalized.trim().split(" ").filter(Boolean)
    const leading = normalized.startsWith(" ") ? " " : ""
    const trailing = normalized.endsWith(" ") ? " " : ""

    return partWords.map((word, index) => {
      const range = words[wordIndex++]
      const isLast = index === partWords.length - 1
      const display = `${index === 0 ? leading : ""}${word}${isLast ? trailing : " "}`
      const start = scale(range?.start ?? 0)
      const end = scale(range?.end ?? 1)
      return (
        <HighlightWord
          key={`${partIndex}-${start}-${end}`}
          start={start}
          end={end}
        >
          {display}
        </HighlightWord>
      )
    })
  })
}

export function ScrollHighlightGroup({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  useScrollHighlight(ref)

  return (
    <div ref={ref} className={cn("scroll-highlight", className)}>
      {children}
    </div>
  )
}

export function ScrollHighlightText({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  const ref = useRef<HTMLParagraphElement>(null)
  useScrollHighlight(ref)

  return (
    <p ref={ref} className={cn("scroll-highlight", className)}>
      <HighlightWords>{flattenHighlightText(children)}</HighlightWords>
    </p>
  )
}
